import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { SECTION_REGISTRY } from '../registry';
import { Search, Plus, Trash2, X } from 'lucide-react';

const CATEGORIES = [
  { id: 'essentials', label: '⭐ Core' },
  { id: 'career', label: '💼 Professional' },
  { id: 'leadership', label: '🌍 Leadership & Volunteer' },
  { id: 'academic', label: '🎓 Research' },
  { id: 'personal', label: '👤 Personal & Creative' },
  { id: 'custom', label: '🧩 Custom' }
];

export default function CustomizeResumeModal({ isOpen, onClose, resume, onEnableSection, onDisableSection, theme }) {
  const [searchQuery, setSearchQuery] = useState('');
  
  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const sectionSettings = resume?.sectionSettings || {};
  const activeSections = resume?.sections || [];

  // Filter sections by search query
  const query = searchQuery.toLowerCase().trim();
  const filteredRegistry = SECTION_REGISTRY.filter(item => {
    if (!query) return true;
    return (
      item.title.toLowerCase().includes(query) ||
      item.category.toLowerCase().includes(query) ||
      (item.recommendedFor || []).some(ind => ind.toLowerCase().includes(query)) ||
      (item.badges || []).some(b => b.toLowerCase().includes(query))
    );
  });

  // Group by category
  const groupedSections = CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = filteredRegistry.filter(item => item.category === cat.id);
    return acc;
  }, {});

  const handleAddCustom = () => {
    // Generate next custom index
    const existingCustomCount = activeSections.filter(id => id.startsWith('custom')).length;
    const newCustomId = `custom_${existingCustomCount + 1}`;
    onEnableSection(newCustomId);
  };

  return createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20
    }}>
      <div style={{
        background: theme === 'dark' ? '#0f0f18' : '#ffffff',
        border: '1px solid var(--border-default)',
        borderRadius: 16, width: '100%', maxWidth: 540,
        height: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border-default)',
          flexShrink: 0
        }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 2px' }}>
              Customize Resume Sections
            </h3>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
              Add or remove optional sections to customize your professional layout.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', display: 'flex', padding: 4
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 20px', flexShrink: 0, borderBottom: '1px solid var(--border-default)' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: theme === 'dark' ? '#1c1c24' : '#f1f5f9',
            borderRadius: 8, padding: '6px 12px', border: '1px solid var(--card-border)'
          }}>
            <Search size={14} color="var(--text-muted)" />
            <input
              type="text"
              placeholder="Search sections (e.g. Certifications, Languages, Software)..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                background: 'transparent', border: 'none', outline: 'none',
                width: '100%', fontSize: 12, color: 'var(--text-primary)'
              }}
            />
          </div>
        </div>

        {/* Content list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {CATEGORIES.map(cat => {
            const list = groupedSections[cat.id] || [];
            if (list.length === 0 && cat.id !== 'custom') return null;

            return (
              <div key={cat.id} style={{ marginBottom: 20 }}>
                <div style={{
                  fontSize: 10, fontWeight: 750, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
                  borderBottom: '1px solid var(--divider)', paddingBottom: 4
                }}>
                  {cat.label}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {list.map(item => {
                    const settings = sectionSettings[item.id];
                    const isEnabled = settings?.enabled;
                    const IconComp = item.icon;

                    return (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 12px', borderRadius: 8,
                          background: theme === 'dark' ? '#14141e' : '#f8fafc',
                          border: '1px solid var(--card-border)',
                          transition: 'all 150ms ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            padding: 6, borderRadius: 6,
                            background: isEnabled ? 'var(--brand-light)' : 'var(--surface-1)',
                            color: isEnabled ? 'var(--brand)' : 'var(--text-muted)',
                            display: 'flex'
                          }}>
                            {IconComp && <IconComp size={16} />}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                              {item.title}
                            </span>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                              {/* Dynamic recommendation badge */}
                              {(() => {
                                const userDomain = (resume?._domain || '').toLowerCase();
                                const isMatch = item.recommendedFor?.some(
                                  ind => userDomain && userDomain.includes(ind.toLowerCase())
                                );
                                if (isMatch) {
                                  return (
                                    <span style={{
                                      fontSize: 8.5, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                                      background: 'rgba(124, 58, 237, 0.12)', color: 'var(--brand)'
                                    }}>
                                      🔥 Role Match
                                    </span>
                                  );
                                }
                                return null;
                              })()}

                              {item.badges?.map((badge, bi) => (
                                <span key={bi} style={{
                                  fontSize: 8.5, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                                  background: badge.includes('Essential') ? 'rgba(239, 68, 68, 0.1)' : 'var(--surface-1)',
                                  color: badge.includes('Essential') ? '#EF4444' : 'var(--text-muted)'
                                }}>
                                  {badge}
                                </span>
                              ))}
                              {item.recommendedFor && item.recommendedFor[0] !== 'all' && (
                                <span style={{ fontSize: 8.5, color: 'var(--text-muted)' }}>
                                  Recommended: {item.recommendedFor.join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Add/Remove Action Button */}
                        {isEnabled ? (
                          <button
                            onClick={() => onDisableSection(item.id)}
                            style={{
                              padding: '5px 10px', fontSize: 10.5, fontWeight: 700,
                              background: 'transparent', border: '1px solid var(--border-default)',
                              color: 'var(--status-danger)', borderRadius: 6, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 4
                            }}
                          >
                            <Trash2 size={11} /> Hide
                          </button>
                        ) : (
                          <button
                            onClick={() => onEnableSection(item.id)}
                            style={{
                              padding: '5px 10px', fontSize: 10.5, fontWeight: 700,
                              background: 'var(--brand)', border: 'none',
                              color: 'white', borderRadius: 6, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 4
                            }}
                          >
                            <Plus size={11} /> Add Section
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* Special Custom Section Spawner */}
                  {cat.id === 'custom' && (
                    <button
                      onClick={handleAddCustom}
                      style={{
                        padding: '10px', fontSize: 11, fontWeight: 700,
                        background: 'transparent', border: '1px dashed var(--border-default)',
                        color: 'var(--brand)', borderRadius: 8, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        marginTop: 4
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-light)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <Plus size={13} /> Add Custom Section
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
